import type {
  ClientOrganization,
  ClientOrganizationRoleEnum,
  ClientOrganizationTagsEnum
} from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC, ReactNode } from 'react';

import { OrganizationAccessState } from '@/shared/components/organization/organizationAccess';
import { OrganizationAccessProvider } from '@/shared/components/organization/OrganizationAccessProvider';
import { useOrganizationAccess } from '@/shared/components/organization/useOrganizationAccess';

type OrganizationAccessGuardProps = {
  /**
   * When provided, the guard self-provides the access context for this
   * organization. Omit it to read the context from an ancestor
   * `OrganizationAccessProvider` (preferred when several guards/buttons share
   * one organization on the same screen).
   */
  organization?: ClientOrganization | undefined;
  roles?: ClientOrganizationRoleEnum[];
  requiredTags?: ClientOrganizationTagsEnum[];
  requiredTagsAny?: ClientOrganizationTagsEnum[];
  ignoreRoles?: boolean;
  children: ReactNode;
  fallback?: ReactNode;
  errorFallback?: ReactNode;
};

const GuardBody: FC<Omit<OrganizationAccessGuardProps, 'organization'>> = ({
  roles,
  requiredTags,
  requiredTagsAny,
  ignoreRoles,
  children,
  fallback,
  errorFallback
}) => {
  const access = useOrganizationAccess({
    roles,
    requiredTags,
    requiredTagsAny,
    ignoreRoles
  });

  switch (access) {
    case OrganizationAccessState.Error: {
      return <>{errorFallback}</>;
    }
    case OrganizationAccessState.Granted: {
      return <>{children}</>;
    }
    default: {
      return <>{fallback}</>;
    }
  }
};

/**
 * Renders `children` when the current user is allowed to see/act on an
 * organization, otherwise `fallback` (default: nothing).
 *
 * The decision is delegated to {@link useOrganizationAccess}; see
 * `resolveOrganizationAccess` for the full rule order. A global organization
 * admin bypasses the per-organization role check, but never `requiredTags` —
 * tags are a hard feature gate on the organization itself.
 *
 * Pass the `organization` prop for standalone usage (the guard then
 * self-provides the access context, even while the organization is still
 * `undefined`/loading). Omit the prop entirely to consume an ancestor
 * `OrganizationAccessProvider` (cheaper when many guards share one org).
 */
export const OrganizationAccessGuard: FC<OrganizationAccessGuardProps> = (
  props
) => {
  const { organization, ...rest } = props;

  // Branch on prop *presence*, not value: a standalone caller may legitimately
  // pass `organization={undefined}` while it is still loading and must still
  // get self-provided context (e.g. so a global admin is granted before load).
  if ('organization' in props) {
    return (
      <OrganizationAccessProvider organization={organization}>
        <GuardBody {...rest} />
      </OrganizationAccessProvider>
    );
  }

  return <GuardBody {...rest} />;
};
